/**
 * Story Context Service for VoiceLive Integration
 * Handles story selection context updates and announcements
 */

import { logger } from '../utils/logger';
import { VoiceLiveClient } from './voiceLiveClient';
import { Story } from '../types/story.types';
import { postJson, API_ENDPOINTS } from '../utils/apiUtils';

export interface StoryContextUpdate {
  storyId: string;
  userAge: number;
  conversationId: string;
  readingMode?: boolean;
}

export interface StoryAnnouncement {
  storyId: string;
  userAge: number;
  conversationId: string;
}

export interface StoryTopicMonitor {
  conversationId: string;
  storyId: string;
  userAge: number;
  userMessage: string;
  assistantResponse: string;
}

export interface StoryContextResponse {
  success: boolean;
  message: string;
  story_title: string;
  context_updated: boolean;
  announcement_ready: boolean;
}

export interface StoryAnnouncementResponse {
  success: boolean;
  announcement_text: string;
  voicelive_instructions: string;
}

export interface StoryTopicMonitorResponse {
  is_on_topic: boolean;
  topic_drift_detected: boolean;
  redirect_needed: boolean;
  reinforcement_instruction: string;
  story_question: string;
}

export class StoryContextService {
  private voiceLiveClient: VoiceLiveClient | null = null;
  private currentStoryId: string | null = null;
  private currentUserAge: number | null = null;
  private currentConversationId: string | null = null;
  private lastUserMessage: string = '';
  private lastAssistantResponse: string = '';
  private topicDriftCallbacks: ((isDrifting: boolean) => void)[] = [];

  constructor(voiceLiveClient?: VoiceLiveClient) {
    this.voiceLiveClient = voiceLiveClient || null;
  }

  /**
   * Subscribe to topic drift events
   */
  onTopicDrift(callback: (isDrifting: boolean) => void): void {
    this.topicDriftCallbacks.push(callback);
  }

  /**
   * Unsubscribe from topic drift events
   */
  offTopicDrift(callback: (isDrifting: boolean) => void): void {
    this.topicDriftCallbacks = this.topicDriftCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Notify topic drift subscribers
   */
  private notifyTopicDrift(isDrifting: boolean): void {
    this.topicDriftCallbacks.forEach(callback => {
      try {
        callback(isDrifting);
      } catch (error) {
        console.error('Error in topic drift callback:', error);
      }
    });
  }

  /**
   * Update VoiceLive client reference
   */
  setVoiceLiveClient(client: VoiceLiveClient): void {
    this.voiceLiveClient = client;
  }

  /**
   * Monitor conversation for topic drift and apply corrections
   */
  async monitorConversationTopic(userMessage: string, assistantResponse: string): Promise<boolean> {
    if (!this.currentStoryId || !this.currentUserAge || !this.currentConversationId) {
      return true; // No story selected, no monitoring needed
    }

    try {
      const monitorRequest: StoryTopicMonitor = {
        conversationId: this.currentConversationId,
        storyId: this.currentStoryId,
        userAge: this.currentUserAge,
        userMessage,
        assistantResponse
      };

      logger.logStoryEvent('topic_monitoring_started', {
        story_id: this.currentStoryId,
        user_message_length: userMessage.length,
        assistant_response_length: assistantResponse.length
      }, this.currentUserAge, this.currentConversationId);

      const response = await postJson(API_ENDPOINTS.STORY_CONTEXT_MONITOR, monitorRequest);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const monitorResponse: StoryTopicMonitorResponse = await response.json();

      // Store messages for next monitoring cycle
      this.lastUserMessage = userMessage;
      this.lastAssistantResponse = assistantResponse;

      // If redirect is needed, update VoiceLive session with reinforcement
      if (monitorResponse.redirect_needed && this.voiceLiveClient && this.voiceLiveClient.isConnected()) {
        await this.applyTopicRedirection(monitorResponse);
        // Notify UI about topic drift
        this.notifyTopicDrift(true);
      } else if (!monitorResponse.topic_drift_detected && this.topicDriftCallbacks.length > 0) {
        // Notify UI that topic is back on track
        this.notifyTopicDrift(false);
      }

      logger.logStoryEvent('topic_monitoring_completed', {
        story_id: this.currentStoryId,
        is_on_topic: monitorResponse.is_on_topic,
        topic_drift_detected: monitorResponse.topic_drift_detected,
        redirect_needed: monitorResponse.redirect_needed
      }, this.currentUserAge, this.currentConversationId);

      return monitorResponse.is_on_topic;

    } catch (error) {
      logger.logErrorEvent('topic_monitoring_failed', error as Error, {
        story_id: this.currentStoryId,
        user_age: this.currentUserAge
      }, this.currentUserAge, this.currentConversationId);
      
      return true; // Assume on-topic if monitoring fails
    }
  }

  /**
   * Apply topic redirection by updating VoiceLive session
   */
  private async applyTopicRedirection(monitorResponse: StoryTopicMonitorResponse): Promise<void> {
    if (!this.voiceLiveClient || !this.voiceLiveClient.isConnected()) {
      return;
    }

    try {
      // Create enhanced instructions with topic redirection
      const redirectionInstructions = `${monitorResponse.reinforcement_instruction}

IMMEDIATE ACTION REQUIRED:
1. Acknowledge briefly but redirect: "That's interesting, but let's focus on your story!"
2. Ask this specific question: "${monitorResponse.story_question}"
3. Do NOT continue the off-topic conversation
4. Guide the child back to story discussion

Remember: You are ONLY here to help with their current story - redirect ALL off-topic conversations immediately!`;

      // Update session with redirection instructions
      this.voiceLiveClient.updateSessionConfig({
        instructions: redirectionInstructions
      });

      logger.logVoiceLiveEvent('topic_redirection_applied', {
        story_question: monitorResponse.story_question,
        reinforcement_applied: true
      }, this.currentUserAge || undefined, this.currentConversationId || undefined);

    } catch (error) {
      logger.logErrorEvent('topic_redirection_failed', error as Error, {
        story_id: this.currentStoryId
      }, this.currentUserAge || undefined, this.currentConversationId || undefined);
    }
  }

  /**
   * Update story context in VoiceLive session when a story is selected
   */
  async updateStoryContext(story: Story, userAge: number, conversationId: string): Promise<StoryContextResponse> {
    try {
      // Store current story context for monitoring
      this.currentStoryId = story.id;
      this.currentUserAge = userAge;
      this.currentConversationId = conversationId;

      // Validate story object and required fields
      if (!story || !story.id || !story.title) {
        throw new Error('Invalid story object: missing id or title');
      }

      if (!userAge || userAge < 6 || userAge > 18) {
        throw new Error('Invalid user age: must be between 6 and 18');
      }

      if (!conversationId) {
        throw new Error('Invalid conversation ID: cannot be empty');
      }

      const updateRequest: StoryContextUpdate = {
        storyId: story.id,
        userAge,
        conversationId,
        readingMode: true
      };

      console.log('StoryContextService: Updating story context for:', {
        storyId: story.id,
        storyTitle: story.title,
        userAge,
        conversationId,
        readingMode: true
      });

      logger.logStoryEvent('story_context_update_started', {
        story_id: story.id,
        story_title: story.title,
        user_age: userAge
      }, userAge, conversationId);

      // Call backend API to get updated context using new API utility
      const response = await postJson(API_ENDPOINTS.STORY_CONTEXT_UPDATE, updateRequest);

      if (!response.ok) {
        const errorText = await response.text();
        console.warn('StoryContextService: Backend context update failed:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const contextResponse: StoryContextResponse = await response.json();

      console.log('StoryContextService: Context update successful:', contextResponse);

      // If we have a VoiceLive client, update its session
      if (this.voiceLiveClient && this.voiceLiveClient.isConnected()) {
        // Get the updated session configuration from the backend
        await this.applyContextToVoiceLiveSession(story, userAge, conversationId);
      } else {
        console.log('StoryContextService: VoiceLive not connected, skipping session update');
      }

      logger.logStoryEvent('story_context_update_completed', {
        story_id: story.id,
        success: contextResponse.success,
        context_updated: contextResponse.context_updated
      }, userAge, conversationId);

      return contextResponse;

    } catch (error) {
      console.error('StoryContextService: Context update failed:', error);
      
      logger.logErrorEvent('story_context_update_failed', error as Error, {
        story_id: story.id,
        user_age: userAge
      }, userAge, conversationId);
      
      // Return a graceful fallback response
      return {
        success: false,
        message: `Story context update failed (backend unavailable): ${(error as Error).message}`,
        story_title: story.title,
        context_updated: false,
        announcement_ready: false
      };
    }
  }

  /**
   * Trigger story announcement in VoiceLive
   */
  async announceStory(story: Story, userAge: number, conversationId: string): Promise<boolean> {
    try {
      const announcementRequest: StoryAnnouncement = {
        storyId: story.id,
        userAge,
        conversationId
      };

      console.log('StoryContextService: Requesting story announcement for:', {
        storyId: story.id,
        storyTitle: story.title,
        userAge
      });

      logger.logStoryEvent('story_announcement_started', {
        story_id: story.id,
        story_title: story.title,
        user_age: userAge
      }, userAge, conversationId);

      // Get announcement text from backend
      const response = await postJson(API_ENDPOINTS.STORY_CONTEXT_ANNOUNCEMENT, announcementRequest);

      if (!response.ok) {
        const errorText = await response.text();
        console.warn('StoryContextService: Backend announcement request failed:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const announcementResponse: StoryAnnouncementResponse = await response.json();

      console.log('StoryContextService: Announcement response:', announcementResponse);

      // Trigger announcement in VoiceLive if connected
      if (this.voiceLiveClient && this.voiceLiveClient.isConnected()) {
        console.log('StoryContextService: Triggering VoiceLive announcement');
        await this.triggerVoiceLiveAnnouncement(announcementResponse, conversationId);
      } else {
        console.log('StoryContextService: VoiceLive not connected, announcement text available but not triggered:', 
          announcementResponse.announcement_text);
      }

      logger.logStoryEvent('story_announcement_completed', {
        story_id: story.id,
        success: announcementResponse.success,
        announcement_length: announcementResponse.announcement_text.length
      }, userAge, conversationId);

      return announcementResponse.success;

    } catch (error) {
      console.error('StoryContextService: Story announcement failed:', error);
      
      logger.logErrorEvent('story_announcement_failed', error as Error, {
        story_id: story.id,
        user_age: userAge
      }, userAge, conversationId);
      
      // Graceful fallback - we can still continue without announcement
      console.log('StoryContextService: Story announcement failed, but story selection can continue');
      return false;
    }
  }

  /**
   * Clear story context when no story is selected
   */
  async clearStoryContext(userAge: number, conversationId: string): Promise<boolean> {
    try {
      // Clear stored context
      this.currentStoryId = null;
      this.currentUserAge = null;
      this.currentConversationId = null;
      this.lastUserMessage = '';
      this.lastAssistantResponse = '';

      logger.logStoryEvent('story_context_clear_started', {
        user_age: userAge
      }, userAge, conversationId);

      // Call backend to clear context
      const response = await postJson(API_ENDPOINTS.STORY_CONTEXT_CLEAR, {
        conversationId: conversationId,
        userAge: userAge
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const clearResponse = await response.json();

      // Reset VoiceLive session to general mode
      if (this.voiceLiveClient && this.voiceLiveClient.isConnected()) {
        this.voiceLiveClient.updateSessionConfig({
          instructions: clearResponse.instructions
        });
      }

      logger.logStoryEvent('story_context_clear_completed', {
        success: clearResponse.success
      }, userAge, conversationId);

      return clearResponse.success;

    } catch (error) {
      logger.logErrorEvent('story_context_clear_failed', error as Error, {
        user_age: userAge
      }, userAge, conversationId);
      
      return false;
    }
  }

  /**
   * Apply story context updates to active VoiceLive session
   */
  private async applyContextToVoiceLiveSession(story: Story, userAge: number, conversationId: string): Promise<void> {
    if (!this.voiceLiveClient || !this.voiceLiveClient.isConnected()) {
      return;
    }

    try {
      // Update session instructions with story context
      const storyInstructions = await this.getStoryContextInstructions(story, userAge);
      
      this.voiceLiveClient.updateSessionConfig({
        instructions: storyInstructions
      });

      // Set appropriate VAD mode for story reading (uses backend VAD API)
      await this.voiceLiveClient.enableReadingMode();

      logger.logStoryEvent('voicelive_session_updated', {
        story_id: story.id,
        instructions_length: storyInstructions.length,
        vad_mode: 'reading'
      }, userAge, conversationId);

    } catch (error) {
      logger.logErrorEvent('voicelive_session_update_failed', error as Error, {
        story_id: story.id
      }, userAge, conversationId);
    }
  }

  /**
   * Trigger announcement using VoiceLive session update with immediate response instructions
   */
  private async triggerVoiceLiveAnnouncement(announcement: StoryAnnouncementResponse, conversationId: string): Promise<void> {
    if (!this.voiceLiveClient || !this.voiceLiveClient.isConnected()) {
      return;
    }

    try {
      // Create instructions that force immediate announcement
      const immediateAnnouncementInstructions = `IMPORTANT: You MUST immediately say this exact message as soon as you receive this instruction:

"${announcement.announcement_text}"

Say EXACTLY those words, nothing more, nothing less. Do not ask questions or add any additional comments. After saying this exact announcement, then wait for the child to respond before saying anything else.

After the announcement, you will be in reading companion mode where you:
1. Wait for the child to speak first
2. Only respond to direct questions
3. Provide helpful, age-appropriate assistance
4. Keep responses brief and supportive
5. Encourage their reading progress

But first, say the announcement: "${announcement.announcement_text}"`;

      // Update session with immediate announcement instructions
      this.voiceLiveClient.updateSessionConfig({
        instructions: immediateAnnouncementInstructions
      });

      // Wait a moment for session update to take effect, then trigger a response
      await new Promise(resolve => setTimeout(resolve, 200));

      // Send a conversation item to trigger the assistant to respond
      const triggerEvent = {
        type: 'conversation.item.create',
        item: {
          id: `trigger_${Date.now()}`,
          type: 'message',
          role: 'user',
          content: [{
            type: 'input_text',
            text: 'Start the announcement now.'
          }]
        }
      };

      // Send the trigger event
      (this.voiceLiveClient as any).sendVoiceLiveEvent(triggerEvent);

      // Commit the conversation item to trigger a response
      await new Promise(resolve => setTimeout(resolve, 100));
      const responseCreateEvent = {
        type: 'response.create',
        response: {
          modalities: ['text', 'audio']
        }
      };

      (this.voiceLiveClient as any).sendVoiceLiveEvent(responseCreateEvent);

      logger.logVoiceLiveEvent('story_announcement_triggered', {
        announcement_text: announcement.announcement_text,
        trigger_method: 'conversation_item_create'
      }, this.currentUserAge || undefined, conversationId);

    } catch (error) {
      logger.logErrorEvent('voicelive_announcement_trigger_failed', error as Error, {
        announcement: announcement.announcement_text
      }, this.currentUserAge || undefined, conversationId);
    }
  }

  /**
   * Get story-specific instructions for VoiceLive
   */
  private async getStoryContextInstructions(story: Story, userAge: number): Promise<string> {
    try {
      // This would typically be generated by the backend, but we can create a basic version here
      const storyPreview = story.content.substring(0, 150) + "...";
      
      let encouragementLevel: string;
      if (userAge <= 8) {
        encouragementLevel = "simple and very encouraging";
      } else if (userAge <= 11) {
        encouragementLevel = "helpful and supportive";
      } else {
        encouragementLevel = "thoughtful and engaging";
      }

        return `You are helping a ${userAge} year old child read "${story.title}".

STORY CONTEXT:
- Title: ${story.title}
- Reading Level: ${story.reading_level}
- Estimated Time: ${story.estimated_read_time} minutes
- Preview: ${storyPreview}
- Themes: ${story.tags.join(', ')}

READING COMPANION ROLE:
- Be ${encouragementLevel} in your responses
- Only respond when directly asked or if child seems confused
- Keep responses brief and age-appropriate
- Help with difficult words when asked
- Encourage reading progress
- Don't interrupt the reading flow

Let the child read at their own pace and provide support only when needed.`;    } catch (error) {
      // Fallback basic instructions
      return `You are helping a ${userAge} year old child read "${story.title}". Be supportive and only respond when they ask questions or need help.`;
    }
  }

  /**
   * Complete story selection flow: context update only (no backend announcement)
   * FIXED: Removed backend announcement API call to prevent overlap with VoiceLive UI events
   */
  async handleStorySelection(story: Story, userAge: number, conversationId: string): Promise<boolean> {
    try {
      console.log('StoryContextService: Starting story selection flow for:', story.title);
      
      logger.logStoryEvent('story_selection_flow_started', {
        story_id: story.id,
        story_title: story.title,
        user_age: userAge
      }, userAge, conversationId);

      // Step 1: Update context (backend for story context, no announcement)
      const contextResponse = await this.updateStoryContext(story, userAge, conversationId);
      
      // If context update failed (backend unavailable), provide offline fallback
      if (!contextResponse.success) {
        console.log('StoryContextService: Backend unavailable, using VoiceLive-only offline mode');
        await this.handleOfflineStorySelection(story, userAge, conversationId);
        return true; // Return success for offline mode
      }

      // NOTE: Reading progress tracker initialization and announcement are now handled
      // by handleStoryAnnouncementWithBackend in App.tsx to avoid duplicate announcements.
      // The /api/voicelive/announcement/start endpoint both initializes the tracker AND
      // sends an announcement, so it should only be called once.
      console.log('StoryContextService: Context updated, announcement will be triggered by App.tsx');

      const overallSuccess = contextResponse.success;

      logger.logStoryEvent('story_selection_flow_completed', {
        story_id: story.id,
        context_updated: contextResponse.success,
        overall_success: overallSuccess
      }, userAge, conversationId);

      return overallSuccess;

    } catch (error) {
      console.error('StoryContextService: Story selection flow failed:', error);
      
      logger.logErrorEvent('story_selection_flow_failed', error as Error, {
        story_id: story.id,
        user_age: userAge
      }, userAge, conversationId);
      
      // Try offline fallback (VoiceLive-only)
      try {
        await this.handleOfflineStorySelection(story, userAge, conversationId);
        return true;
      } catch (fallbackError) {
        console.error('StoryContextService: Offline fallback also failed:', fallbackError);
        return false;
      }
    }
  }

  /**
   * Handle story selection when backend is unavailable (VoiceLive-only mode)
   */
  private async handleOfflineStorySelection(story: Story, userAge: number, conversationId: string): Promise<void> {
    console.log('StoryContextService: Handling story selection in VoiceLive-only offline mode');
    
    // Store context locally for any future operations
    this.currentStoryId = story.id;
    this.currentUserAge = userAge;
    this.currentConversationId = conversationId;

    // VoiceLive-only mode: Let VoiceLive handle the announcement natively
    console.log('StoryContextService: VoiceLive-only mode - no manual announcement injection');

    // If VoiceLive is connected, provide basic context only (no manual announcement)
    if (this.voiceLiveClient && this.voiceLiveClient.isConnected()) {
      console.log('StoryContextService: Updating VoiceLive with story context (no forced announcement)');
      
      const basicInstructions = this.createOfflineVoiceLiveInstructions(story, userAge);
      
      this.voiceLiveClient.updateSessionConfig({
        instructions: basicInstructions
      });

      // REMOVED: Manual announcement injection to prevent overlap with VoiceLive native announcements
      console.log('StoryContextService: VoiceLive will handle announcements through its native response system');
    }

    logger.logStoryEvent('story_selection_offline_handled', {
      story_id: story.id,
      story_title: story.title,
      mode: 'voicelive_native_only',
      manual_announcement_skipped: true
    }, userAge, conversationId);
  }

  /**
   * Create a simple offline announcement
   */
  private createOfflineAnnouncement(story: Story, userAge: number): string {
    if (userAge <= 8) {
      return `Great choice! You picked "${story.title}"! This looks like a really fun story. Let's start reading together!`;
    } else if (userAge <= 11) {
      return `Excellent choice! "${story.title}" looks like an engaging story. I'm here to help if you need any assistance while reading!`;
    } else {
      return `Good choice! "${story.title}" should provide some interesting reading. I'm available to discuss the story or help with any questions you might have.`;
    }
  }

  /**
   * Create basic VoiceLive instructions for offline mode
   */
  private createOfflineVoiceLiveInstructions(story: Story, userAge: number): string {
    return `You are helping a ${userAge} year old child read "${story.title}".

READING COMPANION ROLE:
- Be encouraging and supportive 
- Only respond when directly asked or if child seems confused
- Keep responses brief and age-appropriate
- Help with difficult words when asked
- Encourage reading progress
- Stay focused on the story they're reading

The child has selected "${story.title}" to read. Let them read at their own pace and provide support only when needed.`;
  }

  /**
   * Get reading-specific silence duration to reduce interruptions during story reading
   */
  private getReadingModeSilenceDuration(userAge: number): number {
    // UPDATED: Increased durations to reduce interruptions during reading
    if (userAge <= 8) {
      return 4000;  // 4 seconds for younger children (6-8) - allow sentence completion
    } else if (userAge <= 11) {
      return 3500;  // 3.5 seconds for middle childhood (9-11) - allow phrase completion  
    } else if (userAge <= 14) {
      return 3000;  // 3 seconds for early adolescence (12-14) - allow thought completion
    } else {
      return 2500;  // 2.5 seconds for older teens (15+) - standard reading pause
    }
  }
}

// Export singleton instance
export const storyContextService = new StoryContextService();